// src/api/http.js
import axios from 'axios';

// Base URL for your API
// Works with CRA (.env: REACT_APP_API_URL) or Vite (.env: VITE_API_URL)
const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  process.env.VITE_API_URL ||
  'https://fleet-api.stargaterentals.com';

// This module-level variable holds the current JWT.
// Call setAuthToken(token) after login, and all future requests will
// automatically send Authorization: Bearer <token>.
let authToken = null;

export function getApiBaseUrl() {
  return API_BASE_URL
}

export function getAuthToken() {
  return authToken;
}

export function setAuthToken(token) {
  authToken = token || null;
}

export function clearAuthToken() {
  authToken = null;
}

// Single axios instance used by all API calls
export const api = axios.create({
  baseURL: API_BASE_URL,
});

// Attach Authorization header to every request if we have a token
api.interceptors.request.use(
  (config) => {
    if (authToken) {
      const headers = config.headers || {};
      // Don’t overwrite if caller explicitly set Authorization
      if (!headers.Authorization && !headers.authorization) {
        headers.Authorization = `Bearer ${authToken}`;
      }
      config.headers = headers;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Generic JSON HTTP helper with validation error handling, using axios.
 *
 * - On 400: throws Error with `error.validationErrors = { field: message }`
 * - On other non-OK: throws Error with generic message
 * - On OK: returns parsed JSON (or undefined if expectJson = false)
 *
 * Usage:
 *   await requestJson('/drivers', { method: 'GET' });
 *   await requestJson('/drivers', { method: 'POST', body: { ... } });
 */
export async function requestJson(
  url,
  {
    method = 'GET',
    body,
    headers = {},
    expectJson = true,
  } = {}
) {
  const finalHeaders = { ...headers };
  const isFormData = body instanceof FormData;

  const config = {
    url,
    method,
    headers: finalHeaders,
  };

  if (body !== undefined && body !== null) {
    if (isFormData) {
      // Let axios set the multipart boundary for FormData
      config.data = body;
      // Important: DO NOT set Content-Type manually here
    } else {
      // axios will JSON.stringify for us when content-type is application/json
      config.data = body;
      if (!finalHeaders['Content-Type']) {
        finalHeaders['Content-Type'] = 'application/json';
      }
    }
  }

  try {
    const res = await api(config);

    if (!expectJson) {
      return undefined;
    }

    return res.data;
  } catch (err) {
    // axios wraps non-2xx responses in an error with .response
    if (err.response) {
      const { status, data } = err.response;

      if (status === 400) {
        const validationErrors =
          data && typeof data === 'object' && !Array.isArray(data) ? data : {};

        const error = new Error('Validation failed');
        error.status = 400;
        error.validationErrors = validationErrors;
        throw error;
      }

      const bodyText =
        typeof data === 'string'
          ? data
          : data != null
          ? JSON.stringify(data)
          : '';

      const error = new Error(
        `Request to ${url} failed with status ${status}`
      );
      error.status = status;
      error.body = bodyText;
      throw error;
    }

    // Network / config errors (no response from server)
    const error = new Error(`Network error while requesting ${url}`);
    error.status = 0;
    error.body = '';
    throw error;
  }
}
